/**
 * Patient Registration Workflow Type Definitions
 *
 * This module contains TypeScript interfaces and types specifically for
 * the patient registration workflow, form validation, and UI state management.
 * Based on the patient registration specification and existing dietician patterns.
 *
 * @module types/patientRegistration
 */

import type { ValidationResult, FieldValidation } from './registration';

/**
 * Phone data structure for patient registration
 */
export interface PatientPhoneData {
  number: string;
  country_code: string;
  iso_code: string;
}

/**
 * Address data structure for patient registration
 */
export interface PatientAddressData {
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

/**
 * Patient registration form data (Step 1)
 */
export interface PatientRegistrationFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

/**
 * Patient profile form data (Step 2)
 */
export interface PatientProfileFormData {
  firstName: string;
  lastName: string;
  address: PatientAddressData;
  phone: PatientPhoneData;
  date_of_birth: string; // YYYY-MM-DD format
  photo?: File;
}

/**
 * Complete patient registration data structure
 */
export interface PatientRegistrationData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  address: PatientAddressData;
  phone: PatientPhoneData;
  date_of_birth: string;
  photo?: File;
}

/**
 * Workflow state management for patient registration
 */
export interface PatientRegistrationState {
  currentStep: 1 | 2 | 3;
  formData: Partial<PatientRegistrationData>;
  isLoading: boolean;
  errors: Record<string, string>;
  isSubmitting: boolean;
  registrationId?: string;
  completedSteps: Set<number>;
}

/**
 * Step 1: Registration form state
 */
export interface PatientRegistrationFormState {
  values: PatientRegistrationFormData;
  validation: Record<string, FieldValidation>;
  isValid: boolean;
  isSubmitting: boolean;
}

/**
 * Step 2: Profile form state
 */
export interface PatientProfileFormState {
  values: PatientProfileFormData;
  validation: Record<string, FieldValidation>;
  isValid: boolean;
  isSubmitting: boolean;
}

/**
 * API request type for patient profile update
 * Based on API documentation with snake_case field names
 */
export interface PatientProfileApiRequest {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  address: {
    address: string; // street address
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  phone: {
    number: string;
    country_code: string;
    iso_code: string;
  };
  photo?: {
    data: string; // base64-encoded-string
    filename: string;
    mime_type: string;
  };
  consent: {
    terms_of_service: boolean;
    privacy_policy: boolean;
    data_processing: boolean;
    biometric_processing: boolean;
    health_data_processing: boolean;
  };
}

/**
 * API response type for patient profile
 */
export interface PatientProfileApiResponse {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  address: {
    address: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  phone: {
    number: string;
    country_code: string;
    iso_code: string;
  };
  photo?: string;
  created_at: string;
  updated_at: string;
}

// Re-export Australian states constants from centralized location
export {
  AUSTRALIAN_STATES,
  AUSTRALIAN_STATE_OPTIONS,
  COUNTRY_OPTIONS
} from '../constants/geographic';

/**
 * Storage key for patient registration persistence
 */
export const PATIENT_REGISTRATION_STORAGE_KEY = 'bitewise_patient_registration';

/**
 * Hook return type for patient validation
 */
export interface UsePatientValidationReturn {
  validateEmail: (email: string) => ValidationResult;
  validatePassword: (password: string) => ValidationResult;
  validateName: (name: string, fieldName: string) => ValidationResult;
  validateAddress: (address: PatientAddressData) => ValidationResult;
  validateStreet: (street: string) => ValidationResult;
  validateCity: (city: string) => ValidationResult;
  validateState: (state: string) => ValidationResult;
  validatePostalCode: (postalCode: string) => ValidationResult;
  validatePhone: (phone: PatientPhoneData) => ValidationResult;
  validateDateOfBirth: (dateString: string) => ValidationResult;
  validatePhoto: (file: File) => ValidationResult;
  validateStep: (step: number, formData: Partial<PatientRegistrationData>) => ValidationResult;
}

/**
 * API service response wrapper
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * API error class for patient registration
 */
export class PatientApiError extends Error {
  public status: number;
  public fieldErrors?: Record<string, string>;

  constructor(
    message: string,
    status: number,
    fieldErrors?: Record<string, string>
  ) {
    super(message);
    this.name = 'PatientApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}